<template>
    <div class="page">
        <main class="cv" aria-label="CV of Mostafa Elgayar">
            <aside class="sidebar">
                <header class="identity">
                    <img
                        src="../assets/personal.jpg"
                        class="avatar"
                        alt="Portrait of Mostafa Elgayar"
                        width="160"
                        height="160"
                    />
                    <h1 class="name">
                        {{ data.header.firstName }} <span>{{ data.header.lastName }}</span>
                    </h1>
                    <p class="motto">{{ data.header.motto }}</p>

                    <ul class="social" aria-label="Social links">
                        <li>
                            <a href="https://github.com/Elgayar/" target="_blank" rel="noopener" aria-label="GitHub">
                                <i class="fab fa-github" aria-hidden="true"></i>
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://www.linkedin.com/in/mostafaelgayar/"
                                target="_blank"
                                rel="noopener"
                                aria-label="LinkedIn"
                            >
                                <i class="fab fa-linkedin" aria-hidden="true"></i>
                            </a>
                        </li>
                        <li>
                            <a href="mailto:elgayar@uni-potsdam.de" aria-label="Email">
                                <i class="fas fa-envelope" aria-hidden="true"></i>
                            </a>
                        </li>
                    </ul>

                    <p class="location">
                        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                        <span>{{ data.sidebar.location }}</span>
                    </p>
                </header>

                <section class="block" aria-labelledby="languages-heading">
                    <h2 id="languages-heading">{{ data.sidebar.languages.title }}</h2>
                    <dl class="languages">
                        <div>
                            <dt>{{ data.sidebar.languages.arabic }}</dt>
                            <dd>{{ data.sidebar.languages.levels.native }}</dd>
                        </div>
                        <div>
                            <dt>{{ data.sidebar.languages.english }}</dt>
                            <dd>{{ data.sidebar.languages.levels.fluent }}</dd>
                        </div>
                        <div>
                            <dt>{{ data.sidebar.languages.german }}</dt>
                            <dd>{{ data.sidebar.languages.levels.intermediate }}</dd>
                        </div>
                    </dl>
                </section>

                <section class="block" aria-labelledby="tech-heading">
                    <h2 id="tech-heading">{{ data.sidebar.technologies.title }}</h2>
                    <div v-for="group in techGroups" :key="group.label" class="tech-group">
                        <h3>{{ group.label }}</h3>
                        <ul class="chips">
                            <li v-for="t in group.items" :key="t">{{ t }}</li>
                        </ul>
                    </div>
                </section>

                <section class="block" aria-labelledby="interests-heading">
                    <h2 id="interests-heading">{{ data.sidebar.interests.title }}</h2>
                    <ul class="chips interests">
                        <li>
                            <i class="fas fa-headphones" aria-hidden="true"></i> {{ data.sidebar.interests.music }}
                        </li>
                        <li>
                            <i class="fas fa-basketball-ball" aria-hidden="true"></i>
                            {{ data.sidebar.interests.basketball }}
                        </li>
                        <li><i class="fas fa-tv" aria-hidden="true"></i> {{ data.sidebar.interests.movies }}</li>
                        <li>
                            <i class="fas fa-plane" aria-hidden="true"></i> {{ data.sidebar.interests.travelling }}
                        </li>
                        <li>
                            <i class="fas fa-book-open" aria-hidden="true"></i> {{ data.sidebar.interests.reading }}
                        </li>
                        <li>
                            <i class="fas fa-camera-retro" aria-hidden="true"></i>
                            {{ data.sidebar.interests.photography }}
                        </li>
                    </ul>
                </section>
            </aside>

            <article class="content">
                <header class="hero">
                    <h1 class="hero-name">
                        {{ data.header.firstName }} <span>{{ data.header.lastName }}</span>
                    </h1>
                    <p class="hero-motto">{{ data.header.motto }}</p>
                    <p class="hero-bio">{{ data.header.bio }}</p>
                </header>

                <div class="columns">
                    <section class="cv-section reveal" aria-labelledby="experience-heading">
                        <h2 id="experience-heading">
                            <i class="fas fa-briefcase" aria-hidden="true"></i> {{ data.experience.title }}
                        </h2>
                        <ol class="timeline">
                            <li v-for="job in experience" :key="job.name + job.time">
                                <span class="time">{{ job.time }}</span>
                                <div class="entry">
                                    <a :href="job.url" target="_blank" rel="noopener">
                                        <h4>{{ job.name }}</h4>
                                    </a>
                                    <h5>{{ job.title }}</h5>
                                    <p v-if="job.desc">{{ job.desc }}</p>
                                    <ul v-if="job.highlights && job.highlights.length" class="highlights">
                                        <li v-for="h in job.highlights" :key="h">{{ h }}</li>
                                    </ul>
                                </div>
                            </li>
                        </ol>
                    </section>

                    <section class="cv-section reveal" aria-labelledby="education-heading">
                        <h2 id="education-heading">
                            <i class="fas fa-graduation-cap" aria-hidden="true"></i> {{ data.education.title }}
                        </h2>
                        <ol class="timeline">
                            <li v-for="ed in education" :key="ed.name">
                                <span v-if="ed.time" class="time">{{ ed.time }}</span>
                                <div class="entry">
                                    <a :href="ed.url" target="_blank" rel="noopener">
                                        <h4>{{ ed.name }}</h4>
                                    </a>
                                    <h5>{{ ed.title }}<span v-if="ed.location"> · {{ ed.location }}</span></h5>
                                    <ul v-if="ed.highlights && ed.highlights.length" class="highlights">
                                        <li v-for="h in ed.highlights" :key="h">{{ h }}</li>
                                    </ul>
                                </div>
                            </li>
                        </ol>
                    </section>

                    <section class="cv-section reveal" aria-labelledby="programs-heading">
                        <h2 id="programs-heading">
                            <i class="fas fa-globe" aria-hidden="true"></i> {{ data.programs.title }}
                        </h2>
                        <ol class="timeline">
                            <li v-for="p in programs" :key="p.name">
                                <span class="time">{{ p.time }}</span>
                                <div class="entry">
                                    <h4>{{ p.name }}</h4>
                                    <h5>{{ p.title }}<span v-if="p.location"> · {{ p.location }}</span></h5>
                                </div>
                            </li>
                        </ol>
                    </section>

                    <section class="cv-section reveal" aria-labelledby="side-heading">
                        <h2 id="side-heading">
                            <i class="fas fa-lightbulb" aria-hidden="true"></i> {{ data.sideProjects.title }}
                        </h2>
                        <ol class="timeline">
                            <li v-for="p in sideProjects" :key="p.name">
                                <span class="time">{{ p.time }}</span>
                                <div class="entry">
                                    <a :href="p.url" target="_blank" rel="noopener">
                                        <h4>{{ p.name }}</h4>
                                    </a>
                                    <h5>{{ p.title }}</h5>
                                    <p v-if="p.desc">{{ p.desc }}</p>
                                </div>
                            </li>
                        </ol>
                    </section>

                    <section class="cv-section reveal" aria-labelledby="extras-heading">
                        <h2 id="extras-heading">
                            <i class="fab fa-angellist" aria-hidden="true"></i> {{ data.extracurriculars.title }}
                        </h2>
                        <ol class="timeline">
                            <li>
                                <span class="time">{{ data.extracurriculars.knowunity.time }}</span>
                                <div class="entry">
                                    <a href="https://www.knowunity.com/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.knowunity.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.knowunity.title }}</h5>
                                    <p>{{ data.extracurriculars.knowunity.desc1 }}</p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.slash.time }}</span>
                                <div class="entry">
                                    <a href="https://code.berlin/en/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.slash.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.slash.title }}</h5>
                                    <p>{{ data.extracurriculars.slash.desc1 }}</p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.start.time }}</span>
                                <div class="entry">
                                    <a href="https://www.starthack.eu/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.start.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.start.title }}</h5>
                                    <p>
                                        {{ data.extracurriculars.start.desc1 }}
                                        <a
                                            href="https://debatr.eu/"
                                            target="_blank"
                                            rel="noopener"
                                            class="inline-link"
                                            >{{ data.extracurriculars.start.desc2 }}</a
                                        >
                                        {{ data.extracurriculars.start.desc3 }}
                                    </p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.tqic.time }}</span>
                                <div class="entry">
                                    <a
                                        href="https://techquartier.com/impact_challenge/"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        <h4>{{ data.extracurriculars.tqic.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.tqic.title }}</h5>
                                    <p>
                                        {{ data.extracurriculars.tqic.desc1 }}
                                        <a
                                            href="https://www.youtube.com/watch?v=fLuQlPOjtn4"
                                            target="_blank"
                                            rel="noopener"
                                            class="inline-link"
                                            >{{ data.extracurriculars.tqic.desc2 }}</a
                                        >
                                        {{ data.extracurriculars.tqic.desc3 }}
                                    </p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.tqtt.time }}</span>
                                <div class="entry">
                                    <a href="https://techquartier.com/techtalents/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.tqtt.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.tqtt.title }}</h5>
                                    <p>
                                        {{ data.extracurriculars.tqtt.desc1 }}
                                        <a
                                            href="https://vesgualize.com"
                                            target="_blank"
                                            rel="noopener"
                                            class="inline-link"
                                            >{{ data.extracurriculars.tqtt.desc2 }}</a
                                        >
                                        {{ data.extracurriculars.tqtt.desc3 }}
                                    </p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.ideahack.time }}</span>
                                <div class="entry">
                                    <a href="https://www.businessmeetstech.de/ideahack" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.ideahack.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.ideahack.title }}</h5>
                                    <p>{{ data.extracurriculars.ideahack.desc1 }}</p>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.tedx.time }}</span>
                                <div class="entry">
                                    <a href="https://www.facebook.com/TEDxYouthEBIS/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.tedx.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.tedx.title }}</h5>
                                    <p>{{ data.extracurriculars.tedx.desc1 }}</p>
                                    <ul class="chips small">
                                        <li v-for="point in tedxPoints" :key="point">{{ point }}</li>
                                    </ul>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.google.time }}</span>
                                <div class="entry">
                                    <a href="https://codein.withgoogle.com/archive/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.google.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.google.title }}</h5>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.eoi.time }}</span>
                                <div class="entry">
                                    <a href="https://www.facebook.com/eoi.eg/" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.eoi.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.eoi.title }}</h5>
                                </div>
                            </li>
                            <li>
                                <span class="time">{{ data.extracurriculars.fll.time }}</span>
                                <div class="entry">
                                    <a href="http://www.fllegypt.org/FLL.html" target="_blank" rel="noopener">
                                        <h4>{{ data.extracurriculars.fll.name }}</h4>
                                    </a>
                                    <h5>{{ data.extracurriculars.fll.title }}</h5>
                                </div>
                            </li>
                        </ol>
                    </section>
                </div>
            </article>
        </main>

        <footer class="copyright">© {{ year }} Mostafa Elgayar</footer>
    </div>
</template>

<script>
import english from '../i18n/en.json';

export default {
    name: 'CV',
    data() {
        return {
            data: english,
            year: new Date().getFullYear(),
            techGroups: [
                {
                    label: 'Frontend',
                    items: [
                        'Vue',
                        'React',
                        'Angular',
                        'TypeScript',
                        'JavaScript',
                        'HTML',
                        'CSS',
                        'Sass',
                        'Tailwind',
                        'TanStack'
                    ]
                },
                {
                    label: 'Backend',
                    items: ['Node.js', 'NestJS', 'Python', 'GraphQL', 'Prisma', 'PostgreSQL', 'Redis']
                },
                { label: 'Infra & Services', items: ['Docker', 'AWS', 'Firebase', 'Stripe', 'Sentry'] },
                { label: 'Tooling', items: ['Vite', 'Jest', 'Playwright', 'Figma', 'Git'] }
            ]
        };
    },
    computed: {
        experience() {
            const e = this.data.experience;
            return [
                { ...e.join2, url: 'https://www.join.com' },
                { ...e.hpiStoryLab, url: 'https://hpi.de/en/research/digital-health-cluster.html', desc: e.hpiStoryLab.desc1 },
                { ...e.arcual, url: 'https://www.arcual.art' },
                { ...e.join, url: 'https://www.join.com' },
                { ...e.redi, url: 'https://www.redi-school.org/', desc: e.redi.desc1 },
                { ...e.futury, url: 'https://www.futury.eu/themission/greenenergy', desc: e.futury.desc1 },
                {
                    ...e.db,
                    url:
                        'https://www.deutsche-boerse.com/dbg-en/our-company/insights-people-ideas/Inter-n-view-Anthea-Hohmann-Mostafa-Elgayar-2655246',
                    desc: e.db.desc1
                },
                { ...e.sidestream, url: 'https://sidestream.tech', desc: e.sidestream.desc1 },
                { ...e.windflower, url: 'https://www.energy.rwth-aachen.de/', desc: e.windflower.desc1 },
                {
                    ...e.tinkercubes,
                    url: 'https://www.linkedin.com/company/tinkercubes-llc/',
                    desc: e.tinkercubes.desc1
                }
            ];
        },
        sideProjects() {
            const e = this.data.experience;
            const s = this.data.sideProjects;
            return [
                { ...s.sijilo, url: 'https://sijilo.com', desc: s.sijilo.desc1 },
                { ...e.evenius, url: 'https://www.youtube.com/watch?v=EyNU0OLh4fA', desc: e.evenius.desc1 },
                { ...e.ig, url: 'https://ig.academy/', desc: e.ig.desc1 },
                { ...e.clinichubb, url: 'http://clinichubb.herokuapp.com/', desc: e.clinichubb.desc1 },
                { ...e.freelancing, url: 'https://www.fiverr.com/gegeneo' }
            ];
        },
        tedxPoints() {
            return Object.values(this.data.extracurriculars.tedx.desc2);
        },
        education() {
            const e = this.data.education;
            return [
                { ...e.masters, url: 'https://www.hpi.de/' },
                { ...e.uni, url: 'https://www.rwth-aachen.de/' },
                { ...e.school, url: 'https://www.isc.edu.eg/ebis/' }
            ];
        },
        programs() {
            const p = this.data.programs;
            return [p.globalImpact, p.citris, p.globalTeam, p.startBerlin];
        }
    },
    mounted() {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
        const io = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
        );
        this.$el.querySelectorAll('.reveal').forEach(el => io.observe(el));
    }
};
</script>

<style scoped>
:root,
.page {
    --bg: #fafaf7;
    --surface: #ffffff;
    --ink: #0f172a;
    --ink-soft: #475569;
    --muted: #94a3b8;
    --rule: #e5e7eb;
    --accent: #f97316;
    --accent-soft: rgba(249, 115, 22, 0.12);
    --sidebar-bg: #0f172a;
    --sidebar-ink: #e2e8f0;
    --sidebar-ink-soft: #94a3b8;
    --sidebar-rule: rgba(255, 255, 255, 0.08);
    --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06);
    --radius: 14px;
}

@media (prefers-color-scheme: dark) {
    .page {
        --bg: #0a0a0f;
        --surface: #12131a;
        --ink: #e6e6ec;
        --ink-soft: #a3a8b8;
        --muted: #6b7180;
        --rule: rgba(255, 255, 255, 0.08);
        --accent: #fb923c;
        --accent-soft: rgba(251, 146, 60, 0.14);
        --sidebar-bg: #07070b;
        --sidebar-ink: #e6e6ec;
        --sidebar-ink-soft: #9aa0b1;
        --sidebar-rule: rgba(255, 255, 255, 0.06);
        --shadow: 0 1px 2px rgba(0, 0, 0, 0.4), 0 12px 32px rgba(0, 0, 0, 0.45);
    }
}

.page {
    min-height: 100vh;
    background: var(--bg);
    color: var(--ink);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 15px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: clamp(16px, 4vw, 56px) clamp(16px, 4vw, 56px);
}

.cv {
    max-width: 1200px;
    margin: 0 auto;
    background: var(--surface);
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: var(--shadow);
    display: grid;
    grid-template-columns: minmax(280px, 320px) 1fr;
}

@media (max-width: 900px) {
    .cv {
        grid-template-columns: 1fr;
    }
}

/* Sidebar */
.sidebar {
    background: var(--sidebar-bg);
    color: var(--sidebar-ink);
    padding: 40px 32px;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.identity {
    text-align: center;
}

.avatar {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    margin: 0 auto 20px;
    border: 1px solid var(--sidebar-rule);
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.04);
}

.name {
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0 0 6px;
    text-transform: uppercase;
}

.name span {
    color: var(--accent);
}

.motto {
    color: var(--sidebar-ink-soft);
    font-size: 13px;
    margin: 0 0 18px;
}

.social {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    display: flex;
    justify-content: center;
    gap: 14px;
}

.social a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    color: var(--sidebar-ink);
    font-size: 16px;
    text-decoration: none;
    transition: background 180ms ease, transform 180ms ease, color 180ms ease;
}

.social a:hover,
.social a:focus-visible {
    background: var(--accent);
    color: #0f172a;
    transform: translateY(-2px);
}

.location {
    margin: 0;
    color: var(--sidebar-ink-soft);
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    width: 100%;
}

.block h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--sidebar-ink-soft);
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--sidebar-rule);
}

.languages {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 16px;
    row-gap: 6px;
    margin: 0;
}

.languages > div {
    display: contents;
}

.languages dt {
    color: var(--sidebar-ink);
    font-size: 13px;
}

.languages dd {
    color: var(--sidebar-ink-soft);
    font-size: 13px;
    margin: 0;
    text-align: right;
}

.tech-group + .tech-group {
    margin-top: 14px;
}

.tech-group h3 {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--sidebar-ink-soft);
    margin: 0 0 8px;
}

.chips {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.chips li {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: var(--sidebar-ink);
    border: 1px solid var(--sidebar-rule);
}

.chips.interests li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.chips.interests i {
    color: var(--accent);
    font-size: 11px;
}

/* Content */
.content {
    padding: 56px 56px 64px;
}

@media (max-width: 700px) {
    .content {
        padding: 32px 24px 40px;
    }
    .sidebar {
        padding: 32px 24px;
    }
}

.hero {
    margin-bottom: 48px;
}

.hero-name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(36px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    text-transform: uppercase;
    line-height: 1.05;
}

.hero-name span {
    color: var(--accent);
}

.hero-motto {
    font-size: 16px;
    color: var(--ink-soft);
    margin: 0 0 16px;
    font-weight: 500;
}

.hero-bio {
    font-size: 15px;
    color: var(--ink-soft);
    max-width: 60ch;
    margin: 0;
}

@media (max-width: 900px) {
    .sidebar .identity .name,
    .sidebar .identity .motto {
        display: none;
    }
}

@media (min-width: 901px) {
    .hero-name,
    .hero-motto {
        /* still shown on desktop too — sidebar shows name on mobile only */
    }
}

.columns {
    column-count: 2;
    column-gap: 40px;
}

.columns > .cv-section {
    break-inside: avoid;
    margin-bottom: 48px;
}

.columns > .cv-section:last-child {
    margin-bottom: 0;
}

@media (max-width: 800px) {
    .columns {
        column-count: 1;
    }
}

.cv-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink);
    margin: 0 0 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--rule);
    display: flex;
    align-items: center;
    gap: 10px;
}

.cv-section h2 i {
    color: var(--accent);
    font-size: 14px;
}

.timeline {
    list-style: none;
    padding: 0;
    margin: 0;
    position: relative;
}

.timeline > li {
    position: relative;
    padding-left: 22px;
    padding-bottom: 22px;
    border-left: 1px solid var(--rule);
}

.timeline > li:last-child {
    padding-bottom: 0;
}

.timeline > li::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 6px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-soft);
}

.time {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 4px;
}

.entry h4 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: var(--ink);
    line-height: 1.3;
}

.entry h5 {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-soft);
    margin: 2px 0 6px;
}

.entry p {
    font-size: 14px;
    color: var(--ink-soft);
    margin: 6px 0 0;
}

.highlights {
    margin: 8px 0 0;
    padding-left: 18px;
    color: var(--ink-soft);
    font-size: 13.5px;
}

.highlights li {
    margin-bottom: 4px;
    line-height: 1.5;
}

.highlights li::marker {
    color: var(--accent);
}

.entry a {
    text-decoration: none;
    color: inherit;
    background-image: linear-gradient(var(--accent), var(--accent));
    background-size: 0 1px;
    background-repeat: no-repeat;
    background-position: 0 100%;
    transition: background-size 220ms ease;
}

.entry a:hover {
    background-size: 100% 1px;
}

.inline-link {
    color: var(--accent);
    text-decoration: none;
    border-bottom: 1px dotted currentColor;
}

.chips.small {
    margin-top: 8px;
}

.chips.small li {
    background: var(--accent-soft);
    color: var(--accent);
    border-color: transparent;
    font-size: 11px;
    padding: 3px 9px;
}

/* Reveal animation */
.reveal {
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 480ms ease, transform 480ms ease;
}

.reveal.is-visible {
    opacity: 1;
    transform: none;
}

@media (prefers-reduced-motion: reduce) {
    .reveal {
        opacity: 1;
        transform: none;
        transition: none;
    }
    .social a,
    .entry a {
        transition: none;
    }
}

.copyright {
    text-align: center;
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-top: 24px;
}

/* Print */
@media print {
    .page {
        padding: 0;
        background: #fff;
        color: #000;
    }
    .cv {
        box-shadow: none;
        border-radius: 0;
        grid-template-columns: 280px 1fr;
    }
    .sidebar {
        background: #fff;
        color: #000;
        border-right: 1px solid #ccc;
    }
    .sidebar .name span,
    .hero-name span,
    .cv-section h2 i,
    .chips.interests i,
    .timeline > li::before {
        color: #000;
        background: #000;
    }
    .social,
    .reveal {
        opacity: 1 !important;
        transform: none !important;
    }
    a {
        color: #000 !important;
    }
    .copyright {
        display: none;
    }
}
</style>
